// PrivacyPolicy.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SEO from './SEO/SEO';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO 
        title="Privacy Policy | Xtreme Roofing & Construction"
        description="Learn about how Xtreme Roofing & Construction protects and handles your personal information."
      />
      <PolicyContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Title>Privacy Policy</Title>
        <LastUpdated>Last Updated: January 29, 2024</LastUpdated>

        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          <Content>
            <p>We collect information that you provide directly to us, including:</p>
            <List>
              <ListItem>Name and contact information</ListItem>
              <ListItem>Property address and details</ListItem>
              <ListItem>Project requirements and preferences</ListItem>
              <ListItem>Communication history</ListItem>
              <ListItem>Payment information</ListItem>
            </List>
          </Content>
        </Section>

        <Section>
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <Content>
            <p>We use the collected information for:</p>
            <List>
              <ListItem>Providing roofing and construction services</ListItem>
              <ListItem>Communicating about your project</ListItem>
              <ListItem>Processing payments</ListItem>
              <ListItem>Sending service updates and notifications</ListItem>
              <ListItem>Improving our services</ListItem>
            </List>
          </Content>
        </Section>

        <Section>
          <SectionTitle>3. Information Sharing</SectionTitle>
          <Content>
            <p>We may share your information with:</p>
            <List>
              <ListItem>Service providers and contractors</ListItem>
              <ListItem>Payment processors</ListItem>
              <ListItem>Legal authorities when required</ListItem>
            </List>
            <p>We do not sell your personal information to third parties.</p>
          </Content>
        </Section>

        <Section>
          <SectionTitle>4. Data Security</SectionTitle>
          <Content>
            We implement appropriate security measures to protect your personal information from unauthorized access, alteration, or disclosure.
          </Content>
        </Section>

        <Section>
          <SectionTitle>5. Your Rights</SectionTitle>
          <Content>
            <p>You have the right to:</p>
            <List>
              <ListItem>Access your personal information</ListItem>
              <ListItem>Request corrections to your data</ListItem>
              <ListItem>Request deletion of your information</ListItem>
              <ListItem>Opt-out of marketing communications</ListItem>
            </List>
          </Content>
        </Section>

        <Section>
          <SectionTitle>6. Cookies and Tracking</SectionTitle>
          <Content>
            Our website uses cookies and similar technologies to enhance your browsing experience and analyze website traffic.
          </Content>
        </Section>

        <Section>
          <SectionTitle>7. Children's Privacy</SectionTitle>
          <Content>
            Our services are not intended for children under 13. We do not knowingly collect information from children under 13.
          </Content>
        </Section>

        <Section>
          <SectionTitle>8. Changes to Privacy Policy</SectionTitle>
          <Content>
            We may update this privacy policy periodically. We will notify you of any material changes by posting the new policy on this page.
          </Content>
        </Section>

        <ContactSection>
          <ContactTitle>Privacy Questions?</ContactTitle>
          <ContactInfo>
            <p>For questions about our privacy practices, contact us at:</p>
            <p>Xtreme Roofing & Construction</p>
            <p>Email: sales@xtremeroofingandconstruction.net</p>
            <p>Phone: (817) 655-2768</p>
          </ContactInfo>
        </ContactSection>
      </PolicyContainer>
    </>
  );
};

const PolicyContainer = styled(motion.div)`
  max-width: 800px;
  margin: 4rem auto;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 2.5rem);
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: var(--text-light);
  margin-bottom: 3rem;
  font-size: 0.9rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  color: var(--text);
  margin-bottom: 1rem;
`;

const Content = styled.div`
  color: var(--text-light);
  line-height: 1.6;
  
  p {
    margin-bottom: 1rem;
  }
`;

const List = styled.ul`
  list-style: none;
  padding-left: 1.5rem;
  margin: 1rem 0;
`;

const ListItem = styled.li`
  color: var(--text-light);
  margin-bottom: 0.5rem;
  position: relative;

  &:before {
    content: "•";
    color: var(--primary);
    position: absolute;
    left: -1.5rem;
  }
`;

const ContactSection = styled.div`
  margin-top: 4rem;
  padding: 2rem;
  background: var(--background-light);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
`;

const ContactTitle = styled.h2`
  font-size: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--text);
`;

const ContactInfo = styled.div`
  text-align: center;
  color: var(--text-light);

  p {
    margin-bottom: 0.5rem;
  }
`;

export default PrivacyPolicy;
